import { createBrowserRouter } from "react-router-dom";
import { ProtectedRoute } from "./components/protected-route.tsx";
import { ApprovalsPage } from "./pages/approvals.tsx";
import { ClientsPage } from "./pages/clients.tsx";
import { CompaniesPage } from "./pages/companies.tsx";
import { ContactsPage } from "./pages/contacts.tsx";
import { CrmOverviewPage } from "./pages/crm-overview.tsx";
import { EventsPage } from "./pages/events.tsx";
import { HomePage } from "./pages/home.tsx";
import { JobsPage } from "./pages/jobs.tsx";
import { LeadsPage } from "./pages/leads.tsx";
import { LoginPage } from "./pages/login.tsx";
import { MarketingCampaignsPage } from "./pages/marketing-campaigns.tsx";
import { MarketingContentPage } from "./pages/marketing-content.tsx";
import { MarketingDiscoveryPage } from "./pages/marketing-discovery.tsx";
import { MarketingOutreachPage } from "./pages/marketing-outreach.tsx";
import { MarketingOverviewPage } from "./pages/marketing-overview.tsx";
import { MarketingSeoPage } from "./pages/marketing-seo.tsx";
import { KnowledgeDocumentsPage } from "./pages/knowledge-documents.tsx";
import { KnowledgeOverviewPage } from "./pages/knowledge-overview.tsx";
import { KnowledgeSearchPage } from "./pages/knowledge-search.tsx";
import { DeliveryDocsPage } from "./pages/delivery-docs.tsx";
import { DeliveryMilestonesPage } from "./pages/delivery-milestones.tsx";
import { DeliveryOverviewPage } from "./pages/delivery-overview.tsx";
import { DeliveryQaPage } from "./pages/delivery-qa.tsx";
import { DeliveryReleasePage } from "./pages/delivery-release.tsx";
import { DeliveryRequirementsPage } from "./pages/delivery-requirements.tsx";
import { DeliveryReviewsPage } from "./pages/delivery-reviews.tsx";
import { DeliverySprintsPage } from "./pages/delivery-sprints.tsx";
import { DeliveryTasksPage } from "./pages/delivery-tasks.tsx";
import { OpsOverviewPage } from "./pages/ops-overview.tsx";
import { OpsAgentsPage } from "./pages/ops-agents.tsx";
import { OpsJobsPage } from "./pages/ops-jobs.tsx";
import { OpsSchedulerPage } from "./pages/ops-scheduler.tsx";
import { OpsQueuePage } from "./pages/ops-queue.tsx";
import { OpsToolsPage } from "./pages/ops-tools.tsx";
import { OpsPlannerPage } from "./pages/ops-planner.tsx";
import { OpsCostsPage } from "./pages/ops-costs.tsx";
import { OpsMetricsPage } from "./pages/ops-metrics.tsx";
import { OpsHealthPage } from "./pages/ops-health.tsx";
import { FinanceOverviewPage } from "./pages/finance-overview.tsx";
import { SupportOverviewPage } from "./pages/support-overview.tsx";
import { SupportTicketsPage } from "./pages/support-tickets.tsx";
import { SupportNotificationsPage } from "./pages/support-notifications.tsx";
import { SupportMeetingsPage } from "./pages/support-meetings.tsx";
import { SupportSlaPage } from "./pages/support-sla.tsx";
import { SupportHealthPage } from "./pages/support-health.tsx";
import { SupportActivityPage } from "./pages/support-activity.tsx";
import { FinanceQuotesPage } from "./pages/finance-quotes.tsx";
import { FinanceContractsPage } from "./pages/finance-contracts.tsx";
import { FinanceInvoicesPage } from "./pages/finance-invoices.tsx";
import { FinancePaymentsPage } from "./pages/finance-payments.tsx";
import { FinanceExpensesPage } from "./pages/finance-expenses.tsx";
import { FinanceReportsPage } from "./pages/finance-reports.tsx";
import { NotFoundPage } from "./pages/not-found.tsx";
import { ProjectsPage } from "./pages/projects.tsx";
import { ProposalsPage } from "./pages/proposals.tsx";
import { UnauthorizedPage } from "./pages/unauthorized.tsx";
import { WorkflowsPage } from "./pages/workflows.tsx";
import { AdminOverviewPage } from "./pages/admin-overview.tsx";
import { AdminOrgsPage } from "./pages/admin-orgs.tsx";
import { AdminApiKeysPage } from "./pages/admin-api-keys.tsx";
import { AdminWebhooksPage } from "./pages/admin-webhooks.tsx";
import { AdminSubscriptionPage } from "./pages/admin-subscription.tsx";
import { AdminPluginsPage } from "./pages/admin-plugins.tsx";
import { AdminAuditPage } from "./pages/admin-audit.tsx";
import { AdminUsagePage } from "./pages/admin-usage.tsx";
import { AdminSystemHealthPage } from "./pages/admin-system-health.tsx";

function protect(element: JSX.Element) {
  return <ProtectedRoute>{element}</ProtectedRoute>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/unauthorized", element: <UnauthorizedPage /> },

  { path: "/", element: protect(<HomePage />) },
  { path: "/events", element: protect(<EventsPage />) },
  { path: "/workflows", element: protect(<WorkflowsPage />) },
  { path: "/jobs", element: protect(<JobsPage />) },
  { path: "/approvals", element: protect(<ApprovalsPage />) },

  // Phase 3 — CRM
  { path: "/crm", element: protect(<CrmOverviewPage />) },
  { path: "/crm/companies", element: protect(<CompaniesPage />) },
  { path: "/crm/contacts", element: protect(<ContactsPage />) },
  { path: "/crm/leads", element: protect(<LeadsPage />) },
  { path: "/crm/proposals", element: protect(<ProposalsPage />) },
  { path: "/crm/clients", element: protect(<ClientsPage />) },
  { path: "/crm/projects", element: protect(<ProjectsPage />) },

  // Phase 4 — Marketing
  { path: "/marketing", element: protect(<MarketingOverviewPage />) },
  { path: "/marketing/discovery", element: protect(<MarketingDiscoveryPage />) },
  { path: "/marketing/content", element: protect(<MarketingContentPage />) },
  { path: "/marketing/campaigns", element: protect(<MarketingCampaignsPage />) },
  { path: "/marketing/seo", element: protect(<MarketingSeoPage />) },
  { path: "/marketing/outreach", element: protect(<MarketingOutreachPage />) },

  // Phase 5 — Knowledge
  { path: "/knowledge", element: protect(<KnowledgeOverviewPage />) },
  { path: "/knowledge/documents", element: protect(<KnowledgeDocumentsPage />) },
  { path: "/knowledge/search", element: protect(<KnowledgeSearchPage />) },

  // Phase 6 — Delivery
  { path: "/delivery", element: protect(<DeliveryOverviewPage />) },
  { path: "/delivery/requirements", element: protect(<DeliveryRequirementsPage />) },
  { path: "/delivery/sprints", element: protect(<DeliverySprintsPage />) },
  { path: "/delivery/tasks", element: protect(<DeliveryTasksPage />) },
  { path: "/delivery/milestones", element: protect(<DeliveryMilestonesPage />) },
  { path: "/delivery/reviews", element: protect(<DeliveryReviewsPage />) },
  { path: "/delivery/docs", element: protect(<DeliveryDocsPage />) },
  { path: "/delivery/qa", element: protect(<DeliveryQaPage />) },
  { path: "/delivery/release", element: protect(<DeliveryReleasePage />) },

  // Phase 7 — Operations Platform
  { path: "/ops", element: protect(<OpsOverviewPage />) },
  { path: "/ops/agents", element: protect(<OpsAgentsPage />) },
  { path: "/ops/jobs", element: protect(<OpsJobsPage />) },
  { path: "/ops/scheduler", element: protect(<OpsSchedulerPage />) },
  { path: "/ops/queue", element: protect(<OpsQueuePage />) },
  { path: "/ops/tools", element: protect(<OpsToolsPage />) },
  { path: "/ops/planner", element: protect(<OpsPlannerPage />) },
  { path: "/ops/costs", element: protect(<OpsCostsPage />) },
  { path: "/ops/metrics", element: protect(<OpsMetricsPage />) },
  { path: "/ops/health", element: protect(<OpsHealthPage />) },

  // Phase 8 — Finance & Business Operations
  { path: "/finance", element: protect(<FinanceOverviewPage />) },
  { path: "/finance/quotes", element: protect(<FinanceQuotesPage />) },
  { path: "/finance/contracts", element: protect(<FinanceContractsPage />) },
  { path: "/finance/invoices", element: protect(<FinanceInvoicesPage />) },
  { path: "/finance/payments", element: protect(<FinancePaymentsPage />) },
  { path: "/finance/expenses", element: protect(<FinanceExpensesPage />) },
  { path: "/finance/reports", element: protect(<FinanceReportsPage />) },

  // Phase 9 — Customer Support & Communications
  { path: "/support", element: protect(<SupportOverviewPage />) },
  { path: "/support/tickets", element: protect(<SupportTicketsPage />) },
  { path: "/support/notifications", element: protect(<SupportNotificationsPage />) },
  { path: "/support/meetings", element: protect(<SupportMeetingsPage />) },
  { path: "/support/sla", element: protect(<SupportSlaPage />) },
  { path: "/support/health", element: protect(<SupportHealthPage />) },
  { path: "/support/activity", element: protect(<SupportActivityPage />) },

  // Phase 10 — Admin & Enterprise
  { path: "/admin", element: protect(<AdminOverviewPage />) },
  { path: "/admin/orgs", element: protect(<AdminOrgsPage />) },
  { path: "/admin/api-keys", element: protect(<AdminApiKeysPage />) },
  { path: "/admin/webhooks", element: protect(<AdminWebhooksPage />) },
  { path: "/admin/subscription", element: protect(<AdminSubscriptionPage />) },
  { path: "/admin/plugins", element: protect(<AdminPluginsPage />) },
  { path: "/admin/audit", element: protect(<AdminAuditPage />) },
  { path: "/admin/usage", element: protect(<AdminUsagePage />) },
  { path: "/admin/system-health", element: protect(<AdminSystemHealthPage />) },

  { path: "*", element: <NotFoundPage /> },
]);
